import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import Commons from '../../../Services/Common/Common';
import Global from '../../../Services/Common/Global';
import { showSnackbar } from '../../../Reducer/SnackbarReducer';
import useDelete from '../../../hooks/useDeleteN';
import useFileUpload from '../../../hooks/useFileUpload';
import { createInitial, validateRequired } from '../../../util';
import POP_BASIC from './POP_BASIC';
import { useDelete1, useSave1, useSave2, useSearch1 } from './POP_BASIC_QUERY';
import {
  POP_BASIC_FILE_TYPE,
  buildPopBasicFilePath,
  decodePopBasicFileSegment,
  extractPreviewPathsFromHtml,
  hasPopBasicEditorContent,
  normalizePopBasicLinkUrl,
  normalizePopBasicPreviewPath,
  replacePreviewUrlsInHtml,
} from './POP_BASIC_FileUtil';
import {
  POP_BASIC_DISPLAY_LAYER,
  POP_BASIC_Schema,
  createPopBasicDefaultDispPeriod,
  getPopBasicTypeLabel,
  parsePopBasicList,
} from './POP_BASIC_Schema';

const CreateInitialForm = () => ({
  ...createInitial(POP_BASIC_Schema.fields),
  ...createPopBasicDefaultDispPeriod(),
});

const ResolveSavedPopId = (results, fallback = '') => {
  const r = results?.[0];
  return (
    r?.popId ??
    r?.PopId ??
    r?.data?.popId ??
    r?.data?.PopId ??
    fallback ??
    ''
  );
};

function POP_BASICF() {
  const Dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.BaseInfo);

  const GridRef1 = useRef();
  const [RowData1, SetRowData1] = useState([]);
  const [SelectedRowData1, SetSelectedRowData1] = useState([]);
  const [ChangedRowData1, SetChangedRowData1] = useState([]);
  const [DeletedRowData1, SetDeletedRowData1] = useState([]);
  const [Form, SetForm] = useState(CreateInitialForm);
  const [Filters, SetFilters] = useState({
    popType: '',
    displayLayer: '',
    useYn: '',
    searchText: '',
  });
  const [IsWritePage, SetIsWritePage] = useState(false);
  const [CompanyOptions, SetCompanyOptions] = useState([]);
  const { runAsync: Search1Async } = useSearch1({});
  const saveMutation = useSave1({ userInfo });
  const saveFileMutation = useSave2();
  const deleteMutation = useDelete1();

  const { SaveLogicPreview, SaveLogicFile } = useFileUpload({
    oldFilePathField: 'filePath',
    newFileNameField: 'saveName',
    getTargetPath: (item, popId) =>
      buildPopBasicFilePath(userInfo?.coCd || 'common', popId, item?.saveName),
  });

  const deleteHandler = useDelete({
    gridRef: GridRef1,
    deleteMutation,
    updateDeleted: SetDeletedRowData1,
    updateRowData: SetRowData1,
    deletedRowData: DeletedRowData1,
    rowData: RowData1,
    setChangedRowData: SetChangedRowData1,
  });

  /** 회사 마스터 전체 조회 (COM_BASIC GetComBasicR1) */
  const LoadCompanyOptions = useCallback(async () => {
    try {
      const res = await Commons.Axios(
        'post',
        Global.base.useUrl + Global.urls.Company.getComBasicR1,
        {
          Userid: userInfo?.userid || '',
          AuthCd: Commons.EncryptAES(userInfo?.authCd || '') || '',
          CoCd: Commons.EncryptAES(userInfo?.coCd || '') || '',
        }
      );
      const { isSuccess, data } = res.data ?? {};
      if (!isSuccess) {
        SetCompanyOptions([]);
        return;
      }
      const rows = Array.isArray(data) ? data : [];
      SetCompanyOptions(
        rows
          .map((c) => {
            const value = String(c.coCd ?? c.CoCd ?? '').trim();
            const nm = String(c.coNm ?? c.CoNm ?? value).trim();
            return { value, name: nm ? `${nm} (${value})` : value };
          })
          .filter((o) => o.value)
          .sort((a, b) => a.name.localeCompare(b.name, 'ko'))
      );
    } catch {
      SetCompanyOptions([]);
    }
  }, [userInfo?.authCd, userInfo?.coCd, userInfo?.userid]);

  const SearchLogic1 = useCallback(
    async (searchText = Filters.searchText) => {
      const text = typeof searchText === 'string' ? searchText : Filters.searchText;
      SetFilters((prev) => ({ ...prev, searchText: text }));

      let rows = await Search1Async({
        userInfo,
        search: {
          coCd:
            userInfo?.authCd === 'ADMIN' ? '' : userInfo?.coCd || '',
          searchText: text || '',
        },
      });
      rows = Array.isArray(rows) ? rows : [];
      if (Filters.popType) {
        rows = rows.filter((r) => r.popType === Filters.popType);
      }
      if (Filters.displayLayer) {
        rows = rows.filter((r) => r.displayLayer === Filters.displayLayer);
      }
      if (Filters.useYn) {
        rows = rows.filter((r) => r.useYn === Filters.useYn);
      }
      SetRowData1([...rows]);
    },
    [Filters.displayLayer, Filters.popType, Filters.searchText, Filters.useYn, Search1Async, userInfo]
  );

  useEffect(() => {
    SearchLogic1('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Filters.popType, Filters.displayLayer, Filters.useYn]);

  const IsAdmin = userInfo?.authCd === 'ADMIN';

  useEffect(() => {
    if (IsWritePage && IsAdmin) LoadCompanyOptions();
  }, [IsWritePage, IsAdmin, LoadCompanyOptions]);

  const RefreshLogic1 = useCallback(async () => {
    SetChangedRowData1([]);
    SetDeletedRowData1([]);
    SetSelectedRowData1([]);
    GridRef1?.current?.api?.deselectAll?.();
    await SearchLogic1(Filters.searchText);
  }, [Filters.searchText, SearchLogic1]);

  const AddLogic1 = useCallback(() => {
    SetSelectedRowData1([]);
    SetForm(CreateInitialForm());
    SetIsWritePage(true);
  }, []);

  const HandleRowClick1 = useCallback(
    async (row) => {
      if (!row) return;
      SetSelectedRowData1([row]);
      SetForm({
        ...CreateInitialForm(),
        ...row,
        popTypeNm: row.popTypeNm ?? getPopBasicTypeLabel(row.popType),
        displayLayer: row.displayLayer ?? POP_BASIC_DISPLAY_LAYER,
        targetType:
          row.targetType === 'COMPANY' ? 'COMPANY' : 'ALL',
        targetIndCds: [],
        targetCoCds: parsePopBasicList(row.targetCoCds),
        dispPriority: Number(row.dispPriority ?? 1),
      });
      SetIsWritePage(true);
    },
    []
  );

  const CloseWritePage = useCallback(async () => {
    SetIsWritePage(false);
    SetForm(CreateInitialForm());
    await RefreshLogic1();
  }, [RefreshLogic1]);

  const HandlePopTypeChange = useCallback((popType) => {
    SetForm((prev) => {
      const next = { ...prev, popType };
      if (popType === 'FORCE_CONFIRM') {
        next.isForceClose = 'Y';
        next.isHideToday = 'N';
        next.displayLayer = 'MODAL';
      }
      return next;
    });
  }, []);

  const HandleImageUpload = useCallback(
    async (file) => {
      const timestamp = dayjs().format('YYYYMMDDHHmmss');
      const saveName = `${timestamp}_${file.name}`;
      const fileToSave = new File([file], saveName, { type: file.type });
      return SaveLogicPreview(fileToSave);
    },
    [SaveLogicPreview]
  );

  const ProcessSaveFiles = useCallback(
    async (popId, content) => {
      const coCd = userInfo?.coCd || 'common';
      let nextContent = content;

      const previewPaths = extractPreviewPathsFromHtml(nextContent);
      for (const previewPathRaw of previewPaths) {
        const previewPath = normalizePopBasicPreviewPath(previewPathRaw);
        const saveName = decodePopBasicFileSegment(previewPath.split('/').pop());
        if (!previewPath || !saveName) continue;

        const filePath = buildPopBasicFilePath(coCd, popId, saveName);
        await SaveLogicFile(
          { previewFilePath: previewPath, saveName, filePath: '' },
          popId
        );
        nextContent = replacePreviewUrlsInHtml(
          nextContent,
          previewPathRaw,
          filePath
        );
        await saveFileMutation.runAsync([
          {
            popId,
            fileType: POP_BASIC_FILE_TYPE.INLINE,
            fileName: saveName,
            saveName,
            filePath,
            fileSize: 0,
            sortOrder: 0,
          },
        ]);
      }

      return nextContent;
    },
    [SaveLogicFile, saveFileMutation, userInfo?.coCd]
  );

  const SaveLogic1 = useCallback(async () => {
    const saveRow = {
      ...Form,
      dispPriority: Number(Form.dispPriority ?? 1) || 1,
      linkUrl: normalizePopBasicLinkUrl(Form.linkUrl),
    };

    // 비관리자: 본인 회사에만 노출 (GetPopBasicR2 COMPANY 필터와 동일)
    if (!IsAdmin) {
      const loginCoCd = String(userInfo?.coCd || '').trim();
      if (!loginCoCd) {
        Dispatch(
          showSnackbar({
            message: '회사 코드가 없어 저장할 수 없습니다.',
            type: 'warning',
          })
        );
        return;
      }
      saveRow.targetType = 'COMPANY';
      saveRow.targetIndCds = [];
      saveRow.targetCoCds = [loginCoCd];
    }

    const validation = validateRequired([saveRow], POP_BASIC_Schema);
    if (!validation.valid) {
      Dispatch(showSnackbar({ message: validation.message, type: 'warning' }));
      return;
    }

    if (!hasPopBasicEditorContent(saveRow.content)) {
      Dispatch(showSnackbar({ message: '팝업 내용을 입력하세요.', type: 'warning' }));
      return;
    }

    if (
      IsAdmin &&
      saveRow.targetType === 'COMPANY' &&
      !(saveRow.targetCoCds?.length > 0)
    ) {
      Dispatch(showSnackbar({ message: '노출 회사를 1개 이상 선택하세요.', type: 'warning' }));
      return;
    }

    if (
      saveRow.dispStartDt &&
      saveRow.dispEndDt &&
      dayjs(saveRow.dispEndDt).isBefore(dayjs(saveRow.dispStartDt))
    ) {
      Dispatch(
        showSnackbar({
          message: '노출 종료 일시는 시작 일시 이후여야 합니다.',
          type: 'warning',
        })
      );
      return;
    }

    if (saveRow.popType === 'FORCE_CONFIRM' && saveRow.displayLayer !== 'MODAL') {
      Dispatch(
        showSnackbar({
          message: '강제 동의형 팝업은 중앙 모달로만 노출할 수 있습니다.',
          type: 'warning',
        })
      );
      return;
    }

    if (saveRow.popType === 'FORCE_CONFIRM' && saveRow.isForceClose !== 'Y') {
      saveRow.isForceClose = 'Y';
    }

    try {
      const results = await saveMutation.runAsync([
        {
          ...saveRow,
          displayLayer: saveRow.displayLayer || POP_BASIC_DISPLAY_LAYER,
          popTypeNm: getPopBasicTypeLabel(saveRow.popType),
        },
      ]);
      const failed = (results ?? []).some((r) => r?.isSuccess === false);
      if (failed) return;

      const savedPopId = ResolveSavedPopId(results, saveRow.popId);
      if (!savedPopId) {
        Dispatch(
          showSnackbar({
            message:
              '저장은 완료되었으나 팝업 ID를 확인할 수 없어 파일 저장을 건너뜁니다.',
            type: 'warning',
          })
        );
      } else {
        const updatedContent = await ProcessSaveFiles(savedPopId, saveRow.content);
        if (updatedContent !== saveRow.content) {
          await saveMutation.runAsync([
            {
              ...saveRow,
              popId: savedPopId,
              content: updatedContent,
              displayLayer: saveRow.displayLayer || POP_BASIC_DISPLAY_LAYER,
            },
          ]);
        }
      }

      Dispatch(showSnackbar({ message: '저장되었습니다.', type: 'success' }));
      SetIsWritePage(false);
      SetForm(CreateInitialForm());
      await RefreshLogic1();
    } catch {
      Dispatch(showSnackbar({ message: '저장 중 오류가 발생했습니다.', type: 'error' }));
    }
  }, [Dispatch, Form, IsAdmin, ProcessSaveFiles, RefreshLogic1, saveMutation, userInfo?.coCd]);

  const DeleteSelectedLogic1 = useCallback(async () => {
    if (!SelectedRowData1?.length) {
      Dispatch(
        showSnackbar({ message: '삭제할 팝업을 선택하세요.', type: 'warning' })
      );
      return;
    }
    await deleteHandler.DeleteLogic(SelectedRowData1);
    await RefreshLogic1();
  }, [RefreshLogic1, deleteHandler, Dispatch, SelectedRowData1]);

  const OnSelectionChanged = useCallback(() => {
    const api = GridRef1.current?.api;
    if (!api) return;
    SetSelectedRowData1(api.getSelectedRows?.() ?? []);
  }, []);

  const HandlePopTypeFilterChange = (value) => {
    const next =
      typeof value === 'string' ? value : value?.target?.value ?? '';
    SetFilters((prev) => ({ ...prev, popType: next || '' }));
  };

  const HandleDisplayLayerFilterChange = (value) => {
    const next =
      typeof value === 'string' ? value : value?.target?.value ?? '';
    SetFilters((prev) => ({ ...prev, displayLayer: next || '' }));
  };

  const HandleUseYnFilterChange = (value) => {
    const next =
      typeof value === 'string' ? value : value?.target?.value ?? '';
    SetFilters((prev) => ({ ...prev, useYn: next || '' }));
  };

  return (
    <div className="wrap">
      <POP_BASIC
        userInfo={userInfo}
        GridRef1={GridRef1}
        RowData1={RowData1}
        SelectedRowData1={SelectedRowData1}
        SetSelectedRowData1={SetSelectedRowData1}
        SetChangedRowData1={SetChangedRowData1}
        Form={Form}
        SetForm={SetForm}
        Filters={Filters}
        CompanyOptions={CompanyOptions}
        IsWritePage={IsWritePage}
        HandlePopTypeChange={HandlePopTypeChange}
        HandleImageUpload={HandleImageUpload}
        AddLogic1={AddLogic1}
        HandleRowClick1={HandleRowClick1}
        CloseWritePage={CloseWritePage}
        SaveLogic1={SaveLogic1}
        DeleteSelectedLogic1={DeleteSelectedLogic1}
        SearchLogic1={SearchLogic1}
        RefreshLogic1={RefreshLogic1}
        OnSelectionChanged={OnSelectionChanged}
        HandlePopTypeFilterChange={HandlePopTypeFilterChange}
        HandleDisplayLayerFilterChange={HandleDisplayLayerFilterChange}
        HandleUseYnFilterChange={HandleUseYnFilterChange}
      />
    </div>
  );
}

export default POP_BASICF;
